//@ts-check

import React from 'react';
import { toast } from 'react-toastify';
import { diff } from 'deep-diff';
import classNames from 'classnames';

import { TOAST_HANG_TIME } from './Constants';

export const toastType = {
  success: 'success',
  error: 'error',
  warning: 'warning',
  info: 'info',
};

const renderTitle = (title, message) => {
  if (title && title.length > 0) {
    return (
      <span>
        <strong>{title}</strong>
        {message ? `: ${message}` : undefined}
      </span>
    );
  } else {
    return <span>{message}</span>;
  }
};

export const createToast = (
  level,
  title,
  message,
  autoClose = true,
  hangTime = TOAST_HANG_TIME * 1000,
  onClose = null
) => {
  let options = {
    type: level,
    className: classNames('toast', {
      'toast-info': level === toastType.info,
    }),
  };

  //override errors hangtime to always be 20 secs
  if (options.type === toastType.error) {
    hangTime = 20 * 1000;
  }

  //autoclose takes false or int
  options.autoClose = autoClose ? hangTime : false;
  if (onClose) {
    options.onClose = onClose;
  }

  if (title && title.length > 0) {
    message = renderTitle(title, message);
  }

  return toast(message, options);
};

/**
 * Used in componentDidUpdate(prevProps) to determine if the given
 * `errorField` object exists on the current props and didn't exist
 * in the previous props to display a notification. Handles both
 * single errors (Object) and multiple errors (Array) of the
 * `errorField` on `currentProps`/`prevProps`
 * @param {Object} currentProps Current Component Props
 * @param {Object} prevProps Previous Component Props
 * @param {String} errorField Error property name to check for/compare
 * @returns {Boolean} Whether a notification was created to let the
 * caller know if a clear/reset of the error is needed to prevent
 * repeated notifications
 */
export const errorToast = (
  currentProps,
  prevProps,
  errorField,
  hasErrorCallback
) => createCustomToastFromError(currentProps, prevProps, errorField, null, toastType.error, hasErrorCallback);

/**
 * Used in componentDidUpdate(prevProps) to determine if the given
 * `errorField` object exists on the current props and didn't exist
 * in the previous props to display a notification. Handles both
 * single errors (Object) and multiple errors (Array) of the
 * `errorField` on `currentProps`/`prevProps`
 * @param {Object} currentProps Current Component Props
 * @param {Object} prevProps Previous Component Props
 * @param {String} errorField Error property name to check for/compare
 * @param {String} title
 * @param {String} type
 * @returns {Boolean} Whether a notification was created to let the
 * caller know if a clear/reset of the error is needed to prevent
 * repeated notifications
 */
export const createCustomToastFromError = (
  currentProps,
  prevProps,
  errorField,
  title,
  type,
  hasErrorCallback
) => {
  const currentError = currentProps[errorField];
  const prevError = prevProps[errorField];

  let hasError = false;

  if (currentError && diff(currentError, prevError)) {
    if (!Array.isArray(currentError)) {
      //single error
      createToast(
        type,
        title || currentError.Title,
        currentError.Message,
        true
      );

      hasError = true;
    } else {
      //multiple errors
      currentError.forEach(error => {
        createToast(type, title || error.Title, error.Message, false);
      });

      hasError = true;
    }
  }

  if (hasError && typeof hasErrorCallback === 'function') {
    hasErrorCallback();
  }

  return hasError;
};

/**
 *
 * @param {Object} currentProps
 * @param {Object} prevProps
 * @param {String} field
 * @param {String} message
 * @param {Function} onDiffCallback
 */
export const successToast = (
  currentProps,
  prevProps,
  field,
  message,
  onDiffCallback
) => {
  const current = currentProps[field];
  const prev = prevProps[field];

  let isDiff = false;

  if (current && diff(current, prev)) {
    createToast(toastType.success, 'Success', message || '', true);

    isDiff = true;
  }

  if (isDiff && typeof onDiffCallback === 'function') {
    onDiffCallback();
  }

  return isDiff;
};

/**
 * Shorthand for creating a notification error
 */
export const toastError = message => {
  createToast(toastType.error, 'Error', message);
};

export const toastSuccess = message => {
  createToast(toastType.success, 'Success', message);
};

export const toastInfo = (message, title = 'Results', autoClose = true) => {
  createToast(toastType.info, title, message, autoClose);
};

export const toastErrorFromCatch = (error, defaultMessage) => {
  if (
    !error.response ||
    !error.response.data ||
    !error.response.data.Errors ||
    !error.response.data.Errors[0] ||
    !error.response.data.Errors[0].Title
  ) {
    toastError(defaultMessage);
  } else {
    createToast(
      toastType.error,
      error.response.data.Errors[0].Title,
      error.response.data.Errors[0].Message,
      true
    );
  }
};



// WEBPACK FOOTER //
// ./src/common/ToastHelper.js